import { type Static, Type } from "@sinclair/typebox"
import { Value } from "@sinclair/typebox/value"
import {
  type ArtworkAssetStore,
  ArtworkAssetStoreSchema,
} from "../media/assets"
import type {
  CardLibraryStore,
  DeckLibraryStore,
  HeroLibraryStore,
} from "../schemas"
import {
  CardLibraryStoreSchema,
  DeckLibraryStoreSchema,
  HeroLibraryStoreSchema,
} from "../schemas"
import { IsoTimestampSchema } from "../schemas/common"

export const LIBRARY_ARCHIVE_FORMAT = "craft-force-library-archive"
export const LIBRARY_ARCHIVE_VERSION = 1

export type LibraryArchiveKind = "cards" | "heroes" | "decks"

const CardLibraryArchiveSchema = Type.Object({
  format: Type.Literal(LIBRARY_ARCHIVE_FORMAT),
  version: Type.Literal(LIBRARY_ARCHIVE_VERSION),
  kind: Type.Literal("cards"),
  exportedAt: IsoTimestampSchema,
  records: CardLibraryStoreSchema,
  // Optional — absent in archives created before artwork support was added
  artworkAssets: Type.Optional(ArtworkAssetStoreSchema),
})

const HeroLibraryArchiveSchema = Type.Object({
  format: Type.Literal(LIBRARY_ARCHIVE_FORMAT),
  version: Type.Literal(LIBRARY_ARCHIVE_VERSION),
  kind: Type.Literal("heroes"),
  exportedAt: IsoTimestampSchema,
  records: HeroLibraryStoreSchema,
  // Optional — absent in archives created before artwork support was added
  artworkAssets: Type.Optional(ArtworkAssetStoreSchema),
})

const DeckLibraryArchiveSchema = Type.Object({
  format: Type.Literal(LIBRARY_ARCHIVE_FORMAT),
  version: Type.Literal(LIBRARY_ARCHIVE_VERSION),
  kind: Type.Literal("decks"),
  exportedAt: IsoTimestampSchema,
  records: DeckLibraryStoreSchema,
})

type CardLibraryArchive = Static<typeof CardLibraryArchiveSchema>
type HeroLibraryArchive = Static<typeof HeroLibraryArchiveSchema>
type DeckLibraryArchive = Static<typeof DeckLibraryArchiveSchema>

type LibraryArchiveByKind = {
  cards: CardLibraryArchive
  heroes: HeroLibraryArchive
  decks: DeckLibraryArchive
}

export type LibraryStoreByKind = {
  cards: CardLibraryStore
  heroes: HeroLibraryStore
  decks: DeckLibraryStore
}

export type { ArtworkAssetStore }

type MergeableRecord = {
  updatedAt: string
  remoteSyncedAt?: string
  remoteSyncConflictAt?: string
  remoteSyncFailedAt?: string
}

type MergeImportedRecordsOptions<TRecord extends MergeableRecord> = {
  existingRecords: readonly TRecord[]
  importedRecords: readonly TRecord[]
  getId: (record: TRecord) => string
}

export type MergeImportedRecordsResult<TRecord extends MergeableRecord> = {
  addedCount: number
  records: TRecord[]
  skippedCount: number
  updatedCount: number
}

export type LibraryTransferErrorCode =
  | "LIBRARY_TRANSFER_COMPRESSION_UNAVAILABLE"
  | "LIBRARY_TRANSFER_INVALID_ARCHIVE"
  | "LIBRARY_TRANSFER_KIND_MISMATCH"

export class LibraryTransferError extends Error {
  readonly actualKind?: LibraryArchiveKind
  declare readonly cause?: unknown
  readonly code: LibraryTransferErrorCode
  readonly expectedKind?: LibraryArchiveKind

  constructor(
    code: LibraryTransferErrorCode,
    options: {
      actualKind?: LibraryArchiveKind
      cause?: unknown
      expectedKind?: LibraryArchiveKind
      message?: string
    } = {},
  ) {
    super(options.message ?? code)
    this.actualKind = options.actualKind
    if (options.cause !== undefined) {
      this.cause = options.cause
    }
    this.code = code
    this.expectedKind = options.expectedKind
    this.name = "LibraryTransferError"
  }
}

const ARCHIVE_SCHEMA_BY_KIND = {
  cards: CardLibraryArchiveSchema,
  decks: DeckLibraryArchiveSchema,
  heroes: HeroLibraryArchiveSchema,
} as const

export function createLibraryArchiveFilename(
  kind: LibraryArchiveKind,
  exportedAt = new Date(),
): string {
  return `craft-force-${kind}-${exportedAt.toISOString().slice(0, 10)}.json.gz`
}

export async function createLibraryArchiveBlob<
  TKind extends LibraryArchiveKind,
>(
  kind: TKind,
  records: LibraryStoreByKind[TKind],
  artworkAssets?: ArtworkAssetStore,
): Promise<Blob> {
  const archive: Record<string, unknown> = {
    exportedAt: new Date().toISOString(),
    format: LIBRARY_ARCHIVE_FORMAT,
    kind,
    records,
    version: LIBRARY_ARCHIVE_VERSION,
  }

  if (
    (kind === "cards" || kind === "heroes") &&
    artworkAssets &&
    artworkAssets.length > 0
  ) {
    archive.artworkAssets = artworkAssets
  }

  return gzipText(JSON.stringify(archive, null, 2))
}

export async function parseLibraryArchiveFile<TKind extends LibraryArchiveKind>(
  file: Blob & { name?: string; type?: string },
  kind: TKind,
): Promise<LibraryArchiveByKind[TKind]> {
  const text = await readArchiveText(file)

  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (error) {
    throw new LibraryTransferError("LIBRARY_TRANSFER_INVALID_ARCHIVE", {
      cause: error,
    })
  }

  const actualKind = getArchiveKind(parsed)
  if (actualKind && actualKind !== kind) {
    throw new LibraryTransferError("LIBRARY_TRANSFER_KIND_MISMATCH", {
      actualKind,
      expectedKind: kind,
    })
  }

  const schema = ARCHIVE_SCHEMA_BY_KIND[kind]
  const converted = Value.Convert(schema, parsed)

  if (!Value.Check(schema, converted)) {
    throw new LibraryTransferError("LIBRARY_TRANSFER_INVALID_ARCHIVE")
  }

  return converted as LibraryArchiveByKind[TKind]
}

export function mergeImportedRecords<TRecord extends MergeableRecord>({
  existingRecords,
  importedRecords,
  getId,
}: MergeImportedRecordsOptions<TRecord>): MergeImportedRecordsResult<TRecord> {
  const recordsById = new Map(
    existingRecords.map((record) => [getId(record), record]),
  )

  let addedCount = 0
  let skippedCount = 0
  let updatedCount = 0

  for (const importedRecord of importedRecords) {
    // Strip sync metadata so imported records are treated as "local" on this
    // device. Without this, a record exported from account A (with
    // remoteSyncedAt set) and imported on account B would be dropped by
    // mergeAuthoritativeRemoteRecords when B's server has never seen it.
    const localRecord: TRecord = {
      ...importedRecord,
      remoteSyncedAt: undefined,
      remoteSyncConflictAt: undefined,
      remoteSyncFailedAt: undefined,
    }
    const id = getId(localRecord)
    const existingRecord = recordsById.get(id)

    if (!existingRecord) {
      recordsById.set(id, localRecord)
      addedCount += 1
      continue
    }

    if (existingRecord.updatedAt < localRecord.updatedAt) {
      recordsById.set(id, localRecord)
      updatedCount += 1
      continue
    }

    skippedCount += 1
  }

  return {
    addedCount,
    records: [...recordsById.values()],
    skippedCount,
    updatedCount,
  }
}

function getArchiveKind(value: unknown): LibraryArchiveKind | null {
  if (!value || typeof value !== "object") {
    return null
  }

  const kind = (value as { kind?: unknown }).kind
  if (kind === "cards" || kind === "heroes" || kind === "decks") {
    return kind
  }

  return null
}

async function gzipText(text: string): Promise<Blob> {
  const CompressionStreamCtor = globalThis.CompressionStream
  if (!CompressionStreamCtor) {
    throw new LibraryTransferError("LIBRARY_TRANSFER_COMPRESSION_UNAVAILABLE")
  }

  const compressed = new Blob([text])
    .stream()
    .pipeThrough(new CompressionStreamCtor("gzip"))
  return new Response(compressed).blob()
}

async function gunzipText(blob: Blob): Promise<string> {
  const DecompressionStreamCtor = globalThis.DecompressionStream
  if (!DecompressionStreamCtor) {
    throw new LibraryTransferError("LIBRARY_TRANSFER_COMPRESSION_UNAVAILABLE")
  }

  const stream = new DecompressionStreamCtor("gzip")
  return await new Response(blob.stream().pipeThrough(stream)).text()
}

async function readArchiveText(
  file: Blob & { name?: string; type?: string },
): Promise<string> {
  if (!looksCompressed(file)) {
    return await file.text()
  }

  try {
    return await gunzipText(file)
  } catch (error) {
    if (error instanceof LibraryTransferError) {
      throw error
    }

    throw new LibraryTransferError("LIBRARY_TRANSFER_INVALID_ARCHIVE", {
      cause: error,
    })
  }
}

function looksCompressed(file: { name?: string; type?: string }): boolean {
  return (
    file.name?.endsWith(".gz") === true ||
    file.type === "application/gzip" ||
    file.type === "application/x-gzip"
  )
}
